import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { safeIsoString } from '@/utils/appointmentCivilDate';
import { overlappingCalendarBlocks, type CalendarBlockOverlap } from '@/utils/calendarBlockOverlap';

export interface CalendarBlockOverlapQuery {
  clinicId: string | null | undefined;
  clinicianId: string | null | undefined;
  start: Date | null;
  end: Date | null;
  excludeAppointmentId?: string | null;
  enabled: boolean;
}

type BlockerRow = {
  id: string;
  service_name: string | null;
  starts_at: string;
  ends_at: string;
  service: { is_blocker: boolean } | { is_blocker: boolean }[] | null;
};

function serviceIsBlocker(service: BlockerRow['service']): boolean {
  if (!service) return false;
  if (Array.isArray(service)) return Boolean(service[0]?.is_blocker);
  return Boolean(service.is_blocker);
}

export function useCalendarBlockOverlaps(input: CalendarBlockOverlapQuery) {
  const { clinicId, clinicianId, start, end, excludeAppointmentId, enabled } = input;
  const startIso = safeIsoString(start);
  const endIso = safeIsoString(end);
  const active = Boolean(
    enabled && clinicId && clinicianId && start && end && startIso && endIso && end > start,
  );

  return useQuery({
    queryKey: [
      'appointments',
      'calendar-block-overlap',
      clinicId ?? null,
      clinicianId ?? null,
      startIso,
      endIso,
      excludeAppointmentId ?? null,
    ] as const,
    enabled: active,
    queryFn: async (): Promise<CalendarBlockOverlap[]> => {
      if (!clinicId || !clinicianId || !start || !end || !startIso || !endIso) return [];
      const { data, error } = await supabase
        .from('appointments')
        .select(
          'id, service_name, starts_at, ends_at, service:services!appointments_service_id_fkey(is_blocker)',
        )
        .eq('clinic_id', clinicId)
        .eq('clinician_id', clinicianId)
        .eq('status', 'upcoming')
        .lt('starts_at', endIso)
        .gt('ends_at', startIso);
      if (error) throw error;

      const blocks = ((data ?? []) as unknown as BlockerRow[])
        .filter((row) => serviceIsBlocker(row.service))
        .map((row) => ({
          id: row.id,
          name: row.service_name?.trim() || 'Blokering',
          startsAt: new Date(row.starts_at),
          endsAt: new Date(row.ends_at),
        }));
      return overlappingCalendarBlocks(blocks, start, end, excludeAppointmentId);
    },
  });
}
